interface SwedishHoliday {
  date: Date
  name: string
  /**
   * True for "röda dagar": allmänna helgdagar per lagen (1989:253), plus
   * Midsommarafton, Julafton and Nyårsafton, which arbetstidslagen and
   * semesterlagen treat as Sundays. False for other special days that are
   * ordinary working days, e.g. Valborgsmässoafton.
   */
  isRedDay: boolean
}

/**
 * Calculate Easter Sunday for a given year using the Anonymous Gregorian algorithm.
 */
function getEasterSunday(year: number): Date {
  const a = year % 19
  const b = Math.floor(year / 100)
  const c = year % 100
  const d = Math.floor(b / 4)
  const e = b % 4
  const f = Math.floor((b + 8) / 25)
  const g = Math.floor((b - f + 1) / 3)
  const h = (19 * a + b - d - g + 15) % 30
  const i = Math.floor(c / 4)
  const k = c % 4
  const l = (32 + 2 * e + 2 * i - h - k) % 7
  const m = Math.floor((a + 11 * h + 22 * l) / 451)
  const month = Math.floor((h + l - 7 * m + 114) / 31)
  const day = ((h + l - 7 * m + 114) % 31) + 1

  const date = new Date(year, month - 1, day)
  date.setHours(12, 0, 0, 0)
  return date
}

function addDaysToDate(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  result.setHours(12, 0, 0, 0)
  return result
}

/**
 * Find the Saturday between two dates (inclusive).
 */
function getSaturdayBetween(start: Date, end: Date): Date {
  const current = new Date(start)
  current.setHours(12, 0, 0, 0)
  while (current <= end) {
    if (current.getDay() === 6) {
      return new Date(current)
    }
    current.setDate(current.getDate() + 1)
  }
  return new Date(start)
}

/**
 * Find the Friday before a given date.
 */
function getFridayBefore(date: Date): Date {
  const result = new Date(date)
  result.setHours(12, 0, 0, 0)
  while (result.getDay() !== 5) {
    result.setDate(result.getDate() - 1)
  }
  return result
}

function makeDate(year: number, month: number, day: number): Date {
  const date = new Date(year, month - 1, day)
  date.setHours(12, 0, 0, 0)
  return date
}

/**
 * Get all Swedish holidays for a given year: both "röda dagar" (see
 * `SwedishHoliday.isRedDay`) and other special days that are ordinary working
 * days.
 */
function getSwedishHolidaysForYear(year: number): SwedishHoliday[] {
  const easter = getEasterSunday(year)
  const midsommardagen = getSaturdayBetween(
    makeDate(year, 6, 20),
    makeDate(year, 6, 26),
  )
  const allaHelgonsDag = getSaturdayBetween(
    makeDate(year, 10, 31),
    makeDate(year, 11, 6),
  )

  return [
    { date: makeDate(year, 1, 1), name: "Nyårsdagen", isRedDay: true },
    { date: makeDate(year, 1, 5), name: "Trettondagsafton", isRedDay: false },
    { date: makeDate(year, 1, 6), name: "Trettondedag jul", isRedDay: true },
    { date: addDaysToDate(easter, -3), name: "Skärtorsdagen", isRedDay: false },
    { date: addDaysToDate(easter, -2), name: "Långfredagen", isRedDay: true },
    { date: addDaysToDate(easter, -1), name: "Påskafton", isRedDay: false },
    { date: easter, name: "Påskdagen", isRedDay: true },
    { date: addDaysToDate(easter, 1), name: "Annandag påsk", isRedDay: true },
    {
      date: makeDate(year, 4, 30),
      name: "Valborgsmässoafton",
      isRedDay: false,
    },
    { date: makeDate(year, 5, 1), name: "Första maj", isRedDay: true },
    {
      date: addDaysToDate(easter, 39),
      name: "Kristi himmelsfärdsdag",
      isRedDay: true,
    },
    { date: addDaysToDate(easter, 49), name: "Pingstdagen", isRedDay: true },
    { date: makeDate(year, 6, 6), name: "Nationaldagen", isRedDay: true },
    {
      date: getFridayBefore(midsommardagen),
      name: "Midsommarafton",
      isRedDay: true,
    },
    { date: midsommardagen, name: "Midsommardagen", isRedDay: true },
    {
      date: getFridayBefore(allaHelgonsDag),
      name: "Allhelgonaafton",
      isRedDay: false,
    },
    { date: allaHelgonsDag, name: "Alla helgons dag", isRedDay: true },
    { date: makeDate(year, 12, 24), name: "Julafton", isRedDay: true },
    { date: makeDate(year, 12, 25), name: "Juldagen", isRedDay: true },
    { date: makeDate(year, 12, 26), name: "Annandag jul", isRedDay: true },
    { date: makeDate(year, 12, 31), name: "Nyårsafton", isRedDay: true },
  ]
}

/**
 * Get all Swedish red days between two dates (inclusive).
 * Excludes holidays that fall on a Sunday (already non-working).
 */
export function getSwedishHolidays(
  fromDate: Date,
  toDate: Date,
): SwedishHoliday[] {
  const startYear = fromDate.getFullYear()
  const endYear = toDate.getFullYear()

  const holidays: SwedishHoliday[] = []

  for (let year = startYear; year <= endYear; year++) {
    for (const holiday of getSwedishHolidaysForYear(year)) {
      if (
        holiday.isRedDay &&
        holiday.date >= fromDate &&
        holiday.date <= toDate
      ) {
        holidays.push(holiday)
      }
    }
  }

  return holidays
    .filter((h) => h.date.getDay() !== 0)
    .sort((a, b) => a.date.getTime() - b.date.getTime())
}

function findSwedishHoliday(date: Date): SwedishHoliday | undefined {
  return getSwedishHolidaysForYear(date.getFullYear()).find(
    (h) =>
      h.date.getMonth() === date.getMonth() &&
      h.date.getDate() === date.getDate(),
  )
}

/**
 * Find the red day name for a specific date, or undefined if not a red day.
 */
export function getSwedishHolidayName(date: Date): string | undefined {
  const holiday = findSwedishHoliday(date)
  return holiday?.isRedDay ? holiday.name : undefined
}

/**
 * Find the name of a special day that is not a red day (e.g. Valborgsmässoafton),
 * or undefined if the date isn't one.
 */
export function getSwedishSpecialDayName(date: Date): string | undefined {
  const holiday = findSwedishHoliday(date)
  return holiday && !holiday.isRedDay ? holiday.name : undefined
}

export type { SwedishHoliday }
